import React, { useState, useRef, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { ProjectWithStats, projectV2ShowCompletedTasksAtom } from '../../../../atoms/v3/projectV2Atoms';
import { getProjectColor } from '../../../../utils/colorUtils';
import { ProjectCardV2 } from './ProjectCardV2';
import { ProjectDivider } from './ProjectDivider';

interface ProjectTabsViewProps {
  projects: ProjectWithStats[];
  projectsGrouped: {
    incomplete: ProjectWithStats[];
    completed: ProjectWithStats[];
  };
  showCompletedProjects: boolean;
  onShowCompletedProjectsChange: (show: boolean) => void;
  onTaskToggle: (taskId: string, completed: boolean) => void;
  onTaskClick: (taskId: string) => void;
  onTaskUpdate?: (taskId: string, updates: { projectId?: string; vital?: boolean; urgent?: boolean }) => void;
}

/**
 * 项目标签视图组件
 * 实现横向滚动的项目标签，每个标签对应一个项目
 * 标签下方显示选中项目的任务列表
 */
export const ProjectTabsView: React.FC<ProjectTabsViewProps> = ({
  projects,
  projectsGrouped,
  showCompletedProjects,
  onShowCompletedProjectsChange,
  onTaskToggle,
  onTaskClick,
  onTaskUpdate
}) => {
  const { t } = useTranslation(['v3/projects']);
  const isDark = useAtomValue(isDarkAtom);
  const [showCompletedTasks, setShowCompletedTasks] = useAtom(projectV2ShowCompletedTasksAtom);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    projects.length > 0 ? projects[0].id : null
  );
  const tabsRef = useRef<HTMLDivElement>(null);

  // 当项目列表变化时，确保选中的项目仍然存在
  useEffect(() => {
    if (selectedProjectId && !projects.find(p => p.id === selectedProjectId)) {
      setSelectedProjectId(projects.length > 0 ? projects[0].id : null);
    } else if (!selectedProjectId && projects.length > 0) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  // 获取当前选中的项目
  const selectedProject = projects.find(p => p.id === selectedProjectId);

  // 处理标签点击
  const handleTabClick = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  // 滚动到选中的标签
  const scrollToTab = (projectId: string) => {
    if (!tabsRef.current) return;
    
    const tabElement = tabsRef.current.querySelector(`[data-project-id="${projectId}"]`);
    if (tabElement) {
      tabElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  };

  // 当选中项目变化时，滚动到对应标签
  useEffect(() => {
    if (selectedProjectId) {
      scrollToTab(selectedProjectId);
    }
  }, [selectedProjectId]);



  if (projects.length === 0) {
    return (
      <div className={`
        flex-1 flex items-center justify-center
        ${isDark ? 'text-ios-dark-gray3' : 'text-gray-500'}
      `}>
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <p className="text-sm">{t('emptyState.noProjects')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* 横向滚动的项目标签 */}
      <div className={`
        border-b
        ${isDark ? 'border-ios-dark-gray4' : 'border-gray-200'}
      `}>
        <div
          ref={tabsRef}
          className="flex overflow-x-auto scrollbar-hide px-4 py-3 space-x-2"
          style={{
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {/* 渲染未完成项目标签 */}
          {projectsGrouped.incomplete.map((project) => {
            const isSelected = selectedProjectId === project.id;

            return (
              <button
                key={project.id}
                data-project-id={project.id}
                onClick={() => handleTabClick(project.id)}
                className={`
                  flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                  scroll-snap-align-center
                  ${isSelected
                    ? isDark
                      ? 'bg-blue-900/30 text-blue-400 border border-blue-500/30'
                      : 'bg-blue-100 text-blue-600 border border-blue-200'
                    : isDark
                      ? 'bg-ios-dark-gray5 text-ios-dark-gray2 hover:bg-ios-dark-gray4 border border-transparent'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'
                  }
                `}
                style={{ minWidth: 'fit-content' }}
              >
                <div className="flex items-center space-x-2">
                  {/* 项目颜色指示器 */}
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: getProjectColor(project.id) }}
                  />

                  {/* 项目名称 */}
                  <span className="whitespace-nowrap">
                    {project.title}
                  </span>

                  {/* 任务数量徽章 */}
                  {project.tasks.length > 0 && (
                    <span className={`
                      px-1.5 py-0.5 text-xs rounded-full
                      ${isSelected
                        ? isDark
                          ? 'bg-blue-800/50 text-blue-300'
                          : 'bg-blue-200 text-blue-700'
                        : isDark
                          ? 'bg-ios-dark-gray4 text-ios-dark-gray2'
                          : 'bg-gray-200 text-gray-600'
                      }
                    `}>
                      {project.tasks.length}
                    </span>
                  )}
                </div>
              </button>
            );
          })}

          {/* 已完成项目分割线和控制按钮 */}
          {projectsGrouped.completed.length > 0 && (
            <>
              <div className={`
                flex-shrink-0 w-px h-6 mx-2 self-center
                ${isDark ? 'bg-ios-dark-gray4' : 'bg-gray-300'}
              `} />

              {/* 已完成项目切换按钮 */}
              <button
                onClick={() => {
                  // 如果当前隐藏已完成任务且要显示已完成项目，则自动显示已完成任务
                  if (!showCompletedProjects && !showCompletedTasks) {
                    setShowCompletedTasks(true);
                  }
                  onShowCompletedProjectsChange(!showCompletedProjects);
                }}
                className={`
                  flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200
                  ${showCompletedProjects
                    ? isDark
                      ? 'bg-green-900/30 text-green-400'
                      : 'bg-green-100 text-green-600'
                    : isDark
                      ? 'bg-ios-dark-gray5 text-ios-dark-gray3 hover:bg-ios-dark-gray4'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }
                `}
                title={showCompletedProjects ? '隐藏已完成项目' : '显示已完成项目'}
              >
                {showCompletedProjects ? '隐藏已完成' : `显示已完成 (${projectsGrouped.completed.length})`}
              </button>

              {/* 渲染已完成项目标签（如果显示） */}
              {showCompletedProjects && projectsGrouped.completed.map((project) => {
                const isSelected = selectedProjectId === project.id;

                return (
                  <button
                    key={project.id}
                    data-project-id={project.id}
                    onClick={() => handleTabClick(project.id)}
                    className={`
                      flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                      scroll-snap-align-center opacity-75
                      ${isSelected
                        ? isDark
                          ? 'bg-blue-900/30 text-blue-400 border border-blue-500/30'
                          : 'bg-blue-100 text-blue-600 border border-blue-200'
                        : isDark
                          ? 'bg-ios-dark-gray5 text-ios-dark-gray2 hover:bg-ios-dark-gray4 border border-transparent'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'
                      }
                    `}
                    style={{ minWidth: 'fit-content' }}
                  >
                    <div className="flex items-center space-x-2">
                      {/* 项目颜色指示器 */}
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: getProjectColor(project.id) }}
                      />

                      {/* 项目名称 */}
                      <span className="whitespace-nowrap">
                        {project.title}
                      </span>

                      {/* 任务数量徽章 */}
                      {project.tasks.length > 0 && (
                        <span className={`
                          px-1.5 py-0.5 text-xs rounded-full
                          ${isSelected
                            ? isDark
                              ? 'bg-blue-800/50 text-blue-300'
                              : 'bg-blue-200 text-blue-700'
                            : isDark
                              ? 'bg-ios-dark-gray4 text-ios-dark-gray2'
                              : 'bg-gray-200 text-gray-600'
                          }
                        `}>
                          {project.tasks.length}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* 选中项目的任务列表 */}
      <div className="flex-1 overflow-auto">
        {selectedProject ? (
          <div className="divide-y divide-gray-200 dark:divide-ios-dark-gray4">
            <div className="py-0 first:pt-0 last:pb-0">
              <ProjectCardV2
                project={selectedProject}
                mode="expanded"
                onProjectClick={() => {}} // tabs模式下不需要项目点击
                onTaskClick={onTaskClick}
                onTaskToggle={onTaskToggle}
                onTaskUpdate={onTaskUpdate}
              />
            </div>
          </div>
        ) : (
          <div className={`
            flex-1 flex items-center justify-center
            ${isDark ? 'text-ios-dark-gray3' : 'text-gray-500'}
          `}>
            <p className="text-sm">{t('emptyState.selectProject')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
